import axios from 'axios';
import { toast } from 'react-hot-toast';
import { isAccessControlError, handleAccessControlError } from './accessControlService';
import {
  clearPeDelegatedChildTenantId,
  getCalendarViewChildTenantId,
  getPeDelegatedChildTenantId,
  PE_DELEGATED_CHILD_HEADER,
} from './peDelegatedContext';

/**
 * API Service Configuration
 * 
 * Provides centralized API configuration and interceptors for the TMOS frontend.
 * Handles authentication, error handling, and access control integration.
 * 
 * Features:
 * - Automatic authentication token injection
 * - Intelligent error handling
 * - Access control error integration
 * - Authentication error management
 * - Request/response interceptors
 * 
 * @example
 * ```typescript
 * import api from '@/services/api';
 * const response = await api.get('/users');
 * ```
 */

// Dev: relative path so Vite proxy (see VITE_PROXY_TARGET) is used. Production: VITE_API_URL (e.g. set in GitHub Actions or AWS).
const API_BASE_URL = (import.meta as any).env?.VITE_API_URL
  ? (import.meta as any).env.VITE_API_URL.replace(/\/$/, '') + '/api/v1'
  : import.meta.env.DEV
    ? '/api/v1'
    : 'http://localhost:3000/api/v1';

/**
 * Axios instance configuration
 * 
 * Creates a configured axios instance with base URL and default headers
 * for consistent API communication across the application.
 */
const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 30000, // 30 seconds timeout to prevent hanging requests
});

// Create a separate instance for long-running operations (like deletions)
const longRunningApi = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 120000, // 2 minutes timeout for long-running operations
});

// Apply same interceptors to longRunningApi
longRunningApi.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('auth_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    const url = String(config.url ?? '');
    const calChild = url.includes('calendar') ? getCalendarViewChildTenantId() : null;
    const peChild = getPeDelegatedChildTenantId() || calChild;
    if (peChild) {
      config.headers[PE_DELEGATED_CHILD_HEADER] = peChild;
    } else {
      delete config.headers[PE_DELEGATED_CHILD_HEADER];
    }
    return config;
  },
  (error) => {
    console.error('API Request interceptor error:', error);
    return Promise.reject(error);
  }
);

longRunningApi.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    // Handle access control errors intelligently
    if (isAccessControlError(error)) {
      handleAccessControlError(error);
      return Promise.reject(error);
    }

    const errCodeLr = error.response?.data?.code as string | undefined;
    if (error.response?.status === 403 && errCodeLr === 'PE_PORTFOLIO_DELEGATED_CONTEXT_DENIED') {
      clearPeDelegatedChildTenantId();
      try {
        const url = new URL(window.location.href);
        if (url.searchParams.has('peContext')) {
          url.searchParams.delete('peContext');
          window.history.replaceState({}, '', url.pathname + (url.search ? url.search : '') + url.hash);
        }
      } catch {
        /* ignore */
      }
      toast.error(
        (error.response?.data?.message as string) ||
          'You cannot view that portfolio company in this context.',
        { id: 'pe-delegated-denied' }
      );
    }
    if (error.response?.status === 403 && errCodeLr === 'PE_PORTFOLIO_DELEGATED_READ_ONLY') {
      toast.error(
        (error.response?.data?.message as string) ||
          'Read-only while viewing a portfolio company.',
        { id: 'pe-delegated-readonly' }
      );
    }

    // Handle authentication errors
    if (error.response?.status === 401) {
      const isActivityEndpoint = error.config?.url?.includes('/activity/page-view') || 
                                 error.config?.url?.includes('/activity/error');
      
      if (isActivityEndpoint) {
        return Promise.reject(error);
      }
      
      const isPartnerInvitationError = error.config?.url?.includes('/partner/send-invitation');
      
      if (isPartnerInvitationError) {
        return Promise.reject(error);
      }
      
      const errorMessage = error.response?.data?.error?.message || '';
      const isAuthError = errorMessage.includes('authentication') || 
                         errorMessage.includes('expired');
      
      if (isAuthError) {
        localStorage.removeItem('auth_token');
        if (window.location.pathname !== '/') {
          window.location.href = '/';
        }
      }
    }
    
    const isTimeoutError = error.code === 'ECONNABORTED' || error.message === 'Request aborted';
    const isActivityEndpoint = error.config?.url?.includes('/activity/page-view') || 
                               error.config?.url?.includes('/activity/error');
    if (error.response?.status !== 401 && error.response?.status !== 403 && !isTimeoutError && !isActivityEndpoint) {
      console.error('API Response error:', error.response?.status, error.response?.data);
    }
    
    return Promise.reject(error);
  }
);

/**
 * Request interceptor for authentication
 * 
 * Automatically adds the authentication token to all API requests
 * from localStorage if available.
 */
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('auth_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    const url = String(config.url ?? '');
    const calChild = url.includes('calendar') ? getCalendarViewChildTenantId() : null;
    const peChild = getPeDelegatedChildTenantId() || calChild;
    if (peChild) {
      config.headers[PE_DELEGATED_CHILD_HEADER] = peChild;
    } else {
      delete config.headers[PE_DELEGATED_CHILD_HEADER];
    }
    return config;
  },
  (error) => {
    console.error('API Request interceptor error:', error);
    return Promise.reject(error);
  }
);

/**
 * Response interceptor for error handling
 * 
 * Handles various types of API errors including authentication,
 * access control, and permission errors with intelligent routing.
 */
api.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    // Handle access control errors intelligently
    if (isAccessControlError(error)) {
      handleAccessControlError(error);
      return Promise.reject(error);
    }

    const errCode = error.response?.data?.code as string | undefined;
    if (error.response?.status === 403 && errCode === 'PE_PORTFOLIO_DELEGATED_CONTEXT_DENIED') {
      clearPeDelegatedChildTenantId();
      try {
        const url = new URL(window.location.href);
        if (url.searchParams.has('peContext')) {
          url.searchParams.delete('peContext');
          window.history.replaceState({}, '', url.pathname + (url.search ? url.search : '') + url.hash);
        }
      } catch {
        /* ignore */
      }
      toast.error(
        (error.response?.data?.message as string) ||
          'You cannot view that portfolio company in this context.',
        { id: 'pe-delegated-denied' }
      );
    }
    if (error.response?.status === 403 && errCode === 'PE_PORTFOLIO_DELEGATED_READ_ONLY') {
      toast.error(
        (error.response?.data?.message as string) ||
          'Read-only while viewing a portfolio company.',
        { id: 'pe-delegated-readonly' }
      );
    }

    // Handle authentication errors
    if (error.response?.status === 401) {
      // Activity logging endpoints - completely silent, don't redirect or log
      const isActivityEndpoint = error.config?.url?.includes('/activity/page-view') || 
                                 error.config?.url?.includes('/activity/error');
      
      if (isActivityEndpoint) {
        // Silently ignore 401 errors for activity logging - it's non-critical
        return Promise.reject(error);
      }
      
      // Check if this is a partner invitation error (permission-related)
      const isPartnerInvitationError = error.config?.url?.includes('/partner/send-invitation');
      
      if (isPartnerInvitationError) {
        // Don't redirect for partner invitation errors - let the component handle it
        return Promise.reject(error);
      }
      
      // For other 401 errors, check if it's an authentication error vs permission error
      const errorMessage = error.response?.data?.error?.message || '';
      const isAuthError = errorMessage.includes('token') || 
                         errorMessage.includes('authentication') || 
                         errorMessage.includes('login') ||
                         errorMessage.includes('expired');
      
      if (isAuthError) {
        // Token expired or invalid - only redirect if we're not already on homepage
        localStorage.removeItem('auth_token');
        if (window.location.pathname !== '/') {
          window.location.href = '/';
        }
      } else {
        // Permission-related 401 - don't redirect, let component handle it
      }
    }
    
    // Show 400 Bad Request message to user (e.g. validation or hierarchy scope errors)
    if (error.response?.status === 400 && error.response?.data?.message) {
      const msg = error.response.data.message as string;
      const errorCode = error.response?.data?.errorCode as string | undefined;
      if (errorCode === 'HIERARCHY_OUT_OF_SCOPE') {
        toast.error(msg, { id: 'hierarchy-scope-error' });
      } else {
        toast.error(msg, { id: 'api-400-error' });
      }
    }

    // Only log errors in development mode, and only for non-critical endpoints
    // Timeout errors (ECONNABORTED) are expected for slow endpoints and shouldn't be logged
    // Activity endpoint errors are non-critical and should be completely silent
    const isTimeoutError = error.code === 'ECONNABORTED' || error.message === 'Request aborted';
    const isActivityEndpoint = error.config?.url?.includes('/activity/page-view') || 
                               error.config?.url?.includes('/activity/error');
    const isDevelopment = import.meta.env.DEV;
    
    // Only log in development, and only for non-critical errors
    if (isDevelopment && error.response?.status !== 401 && error.response?.status !== 403 && !isTimeoutError && !isActivityEndpoint) {
      console.error('API Response error:', error.response?.status, error.response?.data);
    }
    
    return Promise.reject(error);
  }
);

export default api;
export { longRunningApi }; 